import type { Identifier } from "ra-core";
import { useGetList } from "ra-core";

// Agreements (task 1.10, D-039): data shapes, lookups and the pure rules of
// the form and the warnings.

export const AGREEMENT_TYPES = [
  "coach",
  "athlete",
  "club",
  "partnership",
  "agency",
  "supply",
  "shareholders",
  "other",
] as const;
export const SAVED_STATUSES = ["draft", "active", "terminated"] as const;
export const RENEWAL_MODES = ["none", "tacit", "explicit"] as const;

export interface Agreement {
  id: Identifier;
  type: (typeof AGREEMENT_TYPES)[number];
  status: (typeof SAVED_STATUSES)[number];
  title: string;
  company_id: number | null;
  contact_id: number | null;
  sales_id: number | null;
  introduced_by_contact_id: number | null;
  start_date: string | null;
  end_date: string | null;
  renewal: (typeof RENEWAL_MODES)[number];
  notice_days: number | null;
  min_purchase_amount: number | null;
  min_purchase_deadline: string | null;
  price_list_id: number | null;
  commission_rule_id: number | null;
  referrer_reward_pct: number | null;
  customer_discount_pct: number | null;
  package_id: number | null;
  terms: string | null;
  drive_url: string | null;
  signed_at: string | null;
  kit_version_id: number | null;
  kit_shipped_at: string | null;
  contribution_amount: number | null;
}

export interface Obligation {
  id?: number;
  agreement_id?: number;
  party: "tenax" | "counterparty";
  description: string;
  due_date: string | null;
  done_at: string | null;
}

export interface FreeItem {
  id?: number;
  agreement_id?: number;
  variant_id: number | null;
  description: string | null;
  qty: number;
  unit_value: number;
}

export interface Package {
  id: number;
  key: string;
  name: string;
  active: boolean;
  position: number;
  contribution_amount: number | null;
  kit_version_id: number | null;
  customer_discount_pct: number | null;
  referrer_reward_pct: number | null;
}

export interface KitVersion {
  id: number;
  key: string;
  name: string;
  active: boolean;
}

export interface AgreementCheck {
  id: Identifier;
  is_over_pool: boolean;
  is_expiring: boolean;
  is_expired: boolean;
  notice_deadline: string | null;
  discount_pct: number | null;
  reward_pct: number | null;
  total_pool_pct: number | null;
}

export interface FirstOrder {
  id: Identifier;
  first_order_deadline: string | null;
  first_order_at: string | null;
  is_overdue: boolean;
}

export const usePackages = () =>
  useGetList<Package>("packages", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
  });

export const useKitVersions = () =>
  useGetList<KitVersion>("kit_versions", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

// The columns tenax_save_agreement writes; the form sends nothing else
export const AGREEMENT_FIELDS = [
  "type",
  "status",
  "title",
  "company_id",
  "contact_id",
  "sales_id",
  "introduced_by_contact_id",
  "start_date",
  "end_date",
  "renewal",
  "notice_days",
  "min_purchase_amount",
  "min_purchase_deadline",
  "price_list_id",
  "commission_rule_id",
  "referrer_reward_pct",
  "customer_discount_pct",
  "package_id",
  "terms",
  "drive_url",
  "signed_at",
  "kit_version_id",
  "kit_shipped_at",
  "contribution_amount",
] as const;

type FormValues = Partial<Agreement> & {
  obligations?: Partial<Obligation>[];
  free_items?: Partial<FreeItem>[];
};

const blank = (value: unknown) =>
  value === undefined || value === null || value === "";

// The arguments of tenax_save_agreement from the form values: empty values
// become null, rows without a description are dropped (D-031: everything
// but the essentials is optional).
export const toSaveArgs = (id: Identifier | null, values: FormValues) => {
  const agreement = Object.fromEntries(
    AGREEMENT_FIELDS.map((field) => [
      field,
      blank(values[field]) ? null : values[field],
    ]),
  );
  const obligations = (values.obligations ?? [])
    .filter((row) => !blank(row.description))
    .map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      party: row.party ?? "counterparty",
      description: String(row.description).trim(),
      due_date: blank(row.due_date) ? null : row.due_date,
      done_at: blank(row.done_at) ? null : row.done_at,
    }));
  const freeItems = (values.free_items ?? [])
    .filter((row) => !blank(row.description) || !blank(row.variant_id))
    .map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      variant_id: blank(row.variant_id) ? null : row.variant_id,
      description: blank(row.description) ? null : row.description,
      qty: Number(row.qty ?? 1),
      unit_value: Number(row.unit_value ?? 0),
    }));
  return {
    p_id: id == null ? null : Number(id),
    p_agreement: agreement,
    p_obligations: obligations,
    p_free_items: freeItems,
  };
};

// Choosing a package prefills its starting values (D-039) only where the
// form is still empty: what was typed for this agreement wins.
export const packageDefaults = (
  values: Partial<Agreement>,
  pkg: Package | undefined,
): Partial<Agreement> => {
  if (!pkg) return {};
  const fields = [
    "contribution_amount",
    "kit_version_id",
    "customer_discount_pct",
    "referrer_reward_pct",
  ] as const;
  return Object.fromEntries(
    fields
      .filter((field) => blank(values[field]) && pkg[field] != null)
      .map((field) => [field, pkg[field]]),
  );
};

export type AgreementWarning =
  | "over_pool"
  | "expiring"
  | "expired"
  | "first_order_overdue";

export const agreementWarnings = (
  check?: Pick<AgreementCheck, "is_over_pool" | "is_expiring" | "is_expired">,
  firstOrder?: Pick<FirstOrder, "is_overdue">,
): AgreementWarning[] =>
  [
    check?.is_over_pool && ("over_pool" as const),
    check?.is_expiring && ("expiring" as const),
    check?.is_expired && ("expired" as const),
    firstOrder?.is_overdue && ("first_order_overdue" as const),
  ].filter((warning): warning is AgreementWarning => !!warning);
